import { sectionRenderer } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { cartSectionsUrl, getCartItemsSectionIds } from '@theme/utilities';

{
  const inFlight = new Set();
  const clampedKeys = new Set();
  let clamping = false;

  const cartContextUrl = () => {
    const url = new URL((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/', location.origin);
    try {
      const previewId = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
    } catch {
      // ignore
    }
    return url;
  };
  const cartUpdateUrl = () => (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update.js';

  const isStackLine = (line) =>
    Boolean(line.dataset.stackLabel || line.dataset.stackBaseVariant);

  const collectOverQuantity = () => {
    const out = [];
    for (const line of document.querySelectorAll('.cart-product')) {
      // Year-slice inventory_quantity is not family stock. Clamping a stack
      // line to 0/1 empties the drawer on a second add (P3G-531).
      if (isStackLine(line)) continue;
      const key = line.dataset.key;
      const tracked = (line.dataset.inventoryManagement || '').length > 0;
      const policy = line.dataset.inventoryPolicy || '';
      const stock = parseInt(line.dataset.inventoryQuantity, 10);
      const input = line.querySelector('input[name="updates[]"]');
      const quantity = input ? parseInt(input.value, 10) : NaN;
      const unavailable = line.classList.contains('cart-product--unavailable');

      if (
        key &&
        tracked &&
        policy !== 'continue' &&
        !unavailable &&
        Number.isFinite(stock) &&
        stock >= 0 &&
        Number.isFinite(quantity) &&
        quantity > stock
      ) {
        out.push({ key, variantId: key.split(':')[0], stock });
      }
    }
    return out;
  };

  const variantIdOf = (line) => (line.dataset.key || '').split(':')[0];

  const setLineNote = (line, text) => {
    const container = line.querySelector('.cart-product__error');
    const message = line.querySelector('.cart-item__error-text');
    if (!container || !message || !text) return;
    message.textContent = text;
    container.classList.remove('hidden');
    container.dataset.stockWarning = 'true';
  };

  const clearLineNote = (line) => {
    const container = line.querySelector('.cart-product__error');
    const message = line.querySelector('.cart-item__error-text');
    if (!container || !message || container.dataset.stockWarning !== 'true') return;
    message.textContent = '';
    container.classList.add('hidden');
    delete container.dataset.stockWarning;
  };

  const applyNotes = () => {
    const over = new Set(collectOverQuantity().map((item) => item.variantId));
    for (const line of document.querySelectorAll('.cart-product')) {
      const variantId = variantIdOf(line);
      if (over.has(variantId)) {
        setLineNote(line, line.dataset.stockWarningText);
      } else if (clampedKeys.has(variantId)) {
        setLineNote(line, line.dataset.stockReducedText);
      } else {
        clearLineNote(line);
      }
    }
  };

  const clampOverQuantity = async () => {
    if (clamping) return;

    const over = collectOverQuantity();
    if (over.length) {
      clamping = true;
      const updates = {};
      over.forEach((item) => {
        updates[item.key] = item.stock;
      });
      const sectionIds = getCartItemsSectionIds();

      try {
        const response = await fetch(cartUpdateUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: sectionIds.join(','), sections_url: cartSectionsUrl() }),
        });
        const data = await response.json();
        over.forEach((item) => clampedKeys.add(item.variantId));
        document.dispatchEvent(
          new CartUpdateEvent(data, 'cart-availability', {
            itemCount: data.item_count,
            sections: data.sections,
            source: 'cart-availability',
          })
        );
      } catch (error) {
        clamping = false;
        applyNotes();
        return;
      }
      clamping = false;
    }

    applyNotes();
  };

  const refreshCartSections = () => {
    for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
      const sectionId = component.dataset.sectionId;
      if (!sectionId || inFlight.has(sectionId)) continue;
      inFlight.add(sectionId);
      const options = { cache: false };
      if (component.closest('cart-drawer-component')) options.url = cartContextUrl();
      sectionRenderer
        .renderSection(sectionId, options)
        .catch(() => {})
        .finally(() => {
          inFlight.delete(sectionId);
          clampOverQuantity();
        });
    }
  };

  const refreshIfDrawerShowsUnavailable = () => {
    for (const line of document.querySelectorAll('cart-drawer-component .cart-product--unavailable')) {
      if (isStackLine(line)) continue;
      refreshCartSections();
      return;
    }
  };

  const onReady = () => {
    refreshIfDrawerShowsUnavailable();
    clampOverQuantity();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('cart:update', () =>
    requestAnimationFrame(() => {
      clampOverQuantity();
      refreshIfDrawerShowsUnavailable();
    })
  );

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCartSections();
  });

  const drawer = document.querySelector('cart-drawer-component');
  if (drawer) {
    drawer.addEventListener('dialog:open', () => {
      // Only re-fetch when unavailable lines need a server refresh — not on every
      // open, which morphs the drawer and was dropping the POS panel.
      refreshIfDrawerShowsUnavailable();
      clampOverQuantity();
    });
  }
}
